import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
    const directorsData = [
        {
            id: 'aashish',
            name: 'Aashish Bohra',
            initials: 'AB',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Purchase & Marketing',
            summary: 'A dynamic procurement strategist with 15+ years of experience. Expert in wood-finish materials, design development, and product innovation.',
            fullBio: `A dynamic procurement strategist and design-focused marketing leader, Aashish Bohra brings over 15 years of strong industry experience to Havn Interior & Global Sourcing. A graduate in Commerce, with academic roots at Vivekananda Engineering College, he has built a powerful foundation in material understanding, sourcing intelligence, and customer-centric design thinking.

Aashish is known for his deep technical knowledge of wood-finish materials such as Veneers, Laminates, and Natural Surface Solutions, along with exceptional skills in Design Development, Concept Creation, and Product Innovation. His unique ability to blend technical expertise with marketing creativity makes him a critical pillar in shaping Havn's high-value interior offerings.`,
            strengths: [
                'Strong understanding of wood and surface materials',
                'Innovation-driven design & concept development',
                'Technology-focused business functioning',
                'High customer communication & voice command presence',
                'Strategic sourcing and cost-competitive procurement'
            ],
            achievements: 'Aashish has earned multiple Awards and Recognitions from leading building-material pioneers such as Century, Greenply, and others—consistently achieving the highest sales targets and driving exceptional performance across all assigned regions.',
            closing: 'With his visionary approach and strong operational mastery, Aashish continues to elevate Havn Interior & Global Sourcing\'s product ecosystem, vendor partnerships, and market leadership.'
        },
        {
            id: 'kunal',
            name: 'Kunal I. Tater',
            initials: 'KT',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Finance & Strategic Operations',
            summary: 'An analytical business leader with 15+ years of experience in finance, cost management, and strategic operations. Certified by Central Vigilance Commission.',
            fullBio: `An analytical, disciplined, and innovation-driven business leader, Kunal I. Tater serves as the Finance and Strategic Operations head at Havn Interior & Global Sourcing. Educated in Telecommunication Engineering from SRM Institute, Tamil Nadu, he brings a powerful blend of technical expertise, financial discipline, and design-driven operational understanding.

With over 15 years of diversified business experience, Kunal has played a key role in steering multiple ventures in Finance, Cost Management, Sales, Marketing, and Design Development. His ability to read markets, manage risk, optimise costs, and build strong operational frameworks makes him one of the core pillars of the organisation.`,
            strengths: [
                'Cost optimisation & financial planning',
                'Design development & product structuring',
                'Strategic sales & operational leadership',
                'Venture development and business scalability',
                'Technology-driven process improvements'
            ],
            achievements: 'Kunal is a certified professional recognized by the Central Vigilance Commission, honoured for demonstrating the Highest Standards of Honesty and Integrity—a rare and prestigious acknowledgement in India\'s corporate and governance ecosystem.',
            additional: `He also serves as a Director at RBSHAH Group (est. 1981), where he drives cost strategy, finance management, sales oversight, and new venture development across:

• EXIM Trade – Duty Credit Scrips
• Multi-brand Retail Stores
• Building & Interior Materials
• Emerging Business Sectors`,
            closing: 'With a sharp business mind and a principled leadership approach, Kunal continues to guide both Havn Interior & RBSHAH Group toward sustained growth, transparency, and future-ready expansion.'
        },
        {
            id: 'saargi',
            name: 'Saargi Jain',
            initials: 'SJ',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Marketing & Design Development',
            summary: 'A creative design strategist with MBA in Finance and professional Interior Design training. National Award winner recognized by the Union Minister of Home Affairs.',
            fullBio: `A creative design strategist and marketing leader, Saargi Jain brings a powerful blend of aesthetics, functionality, and financial precision to Havn Interior & Global Sourcing. With an MBA in Finance (Ethiraj College, Chennai) and professional Interior Design training from NIIT Fashion, she delivers design solutions that balance modern elegance with cost efficiency.

With 5 years of specialised experience, Saargi excels in developing high-performance design concepts across:`,
            strengths: [
                'Kitchen Design & Modular Solutions',
                'Terrace & Outdoor Concepts',
                'Doors & Bathroom Design',
                'Complete Room Décor & Visual Styling'
            ],
            achievements: 'Known for her ability to resolve complex design challenges, calculate precise cost structures, and transform concepts into market-ready designs, she strengthens Havn\'s creative and customer experience leadership. Saargi is also a National Award winner, recognised for excellence in the Fastest Growing Indian Company category by the Union Minister of Home Affairs.',
            closing: 'Her design intelligence, analytical mindset, and customer-centric approach make her a vital pillar of Havn Interior & Global Sourcing\'s brand identity and innovation roadmap.'
        },
        {
            id: 'tarun',
            name: 'Tarun I. Jain',
            initials: 'TJ',
            title: 'President – RBSHAH Group',
            role: 'Partner – Havn Interior and Global Sourcing',
            summary: 'A visionary leader with 20+ years of multi-sector experience. National Award honoree recognized by Union Cabinet Minister.',
            fullBio: `With a legacy of leadership, innovation, and strategic growth, Tarun I. Jain stands at the forefront of RBSHAH Group's expansion into diverse and future-ready business verticals. A graduate in B.A. Economics from Loyola College, he brings over 20 years of multi-sector business experience, marked by strong collaborations, milestone achievements, and a deep understanding of modern business culture.

Tarun's journey is defined by a consistent pursuit of excellence. He is a National Award honoree, recognised by the Union Cabinet Minister of Petroleum & Natural Gas and the Lieutenant Governor of Puducherry. His impactful contributions have also earned accolades and certifications from leading PSUs and multinational organisations.`,
            rbshahResponsibilities: [
                'EXIM Trade & Duty Credit Scrips',
                'Multi-brand Retail Operations',
                'Building & Interior Material Solutions',
                'Expansion into New Ventures and Emerging Business Models'
            ],
            havnResponsibilities: [
                'Global sourcing partnerships',
                'Expansion of premium interior solutions',
                'Cost-efficient procurement frameworks',
                'Strategic collaborations across industries',
                'Development of new international business channels'
            ],
            closing: 'Driven by a forward-thinking mindset and a commitment to excellence, Tarun continues to position RBSHAH Group as a trusted, progressive, and innovation-led enterprise in India\'s evolving business landscape.'
        }
    ];

    useEffect(() => {
        // Hero animations with more dynamics
        gsap.fromTo('.about-hero-title',
            {
                y: 60,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1.2,
                ease: 'power4.out'
            }
        );

        gsap.fromTo('.about-hero-subtitle',
            {
                y: 40,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                delay: 0.3,
                ease: 'power3.out'
            }
        );

        // Animate hero stats with stagger and scale
        gsap.fromTo('.hero-stat-item',
            {
                y: 30,
                opacity: 0,
                scale: 0.9
            },
            {
                y: 0,
                opacity: 1,
                scale: 1,
                duration: 0.8,
                stagger: 0.15,
                delay: 0.5,
                ease: 'back.out(1.7)'
            }
        );

        // Counter animation for stats
        const animateCounter = (element, target) => {
            gsap.to(element, {
                innerHTML: target,
                duration: 2,
                delay: 0.5,
                snap: { innerHTML: 1 },
                ease: 'power1.inOut',
                onUpdate: function() {
                    element.innerHTML = Math.ceil(element.innerHTML) + '+';
                }
            });
        };

        const yearCounter = document.querySelector('.counter-years');
        const studioCounter = document.querySelector('.counter-studios');
        const projectCounter = document.querySelector('.counter-projects');
        
        if (yearCounter) animateCounter(yearCounter, 25);
        if (studioCounter) animateCounter(studioCounter, 8);
        if (projectCounter) animateCounter(projectCounter, 48);

        // Parallax effect for hero background
        gsap.to('.about-hero', {
            backgroundPosition: '50% 100%',
            ease: 'none',
            scrollTrigger: {
                trigger: '.about-hero',
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });

        // Fade in sections with slide and opacity
        gsap.utils.toArray('.fade-in-section').forEach((section) => {
            gsap.fromTo(section,
                {
                    y: 60,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 85%',
                        toggleActions: 'play none none reverse'
                    },
                    duration: 1,
                    ease: 'power3.out'
                }
            );
        });

        // Enhanced stagger animations with scale and opacity
        gsap.utils.toArray('.stagger-item').forEach((container) => {
            const items = container.querySelectorAll('.stagger-child');
            gsap.fromTo(items,
                {
                    y: 50,
                    opacity: 0,
                    scale: 0.95
                },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    scrollTrigger: {
                        trigger: container,
                        start: 'top 80%',
                        toggleActions: 'play none none reverse'
                    },
                    duration: 0.8,
                    stagger: 0.12,
                    ease: 'power3.out'
                }
            );
        });

        // Animate section titles with slide from left
        gsap.utils.toArray('.section-title').forEach((title) => {
            gsap.fromTo(title,
                {
                    x: -50,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    scrollTrigger: {
                        trigger: title,
                        start: 'top 90%',
                    },
                    duration: 0.8,
                    ease: 'power2.out'
                }
            );
        });

        // Reveal animation for timeline items with alternating slides
        gsap.utils.toArray('.timeline-item').forEach((item, index) => {
            gsap.fromTo(item,
                {
                    x: index % 2 === 0 ? -40 : 40,
                    opacity: 0
                },
                {
                    x: 0,
                    opacity: 1,
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 85%',
                    },
                    duration: 0.8,
                    ease: 'power2.out'
                }
            );
        });

        // Add hover effects to studio icons (floating animation)
        gsap.utils.toArray('.studio-icon').forEach((icon, index) => {
            gsap.to(icon, {
                y: -10,
                duration: 1.5 + (index * 0.1),
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut',
                delay: index * 0.2
            });
        });

        // Add subtle pulse to value icons
        gsap.utils.toArray('.value-icon').forEach((icon) => {
            gsap.to(icon, {
                scale: 1.1,
                duration: 1.5,
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut'
            });
        });
    }, []);

    return (
        <div>
            <Helmet>
                <title>About Us - Hav'n Interiors & Global Sourcing</title>
                <meta name="description" content="Learn about Hav'n Interiors, Chennai's premier design destination backed by 25 years of global Sourcing expertise. Discover our vision, mission, and design philosophy." />
                <meta name="keywords" content="interior design company, Chennai design studio, luxury interiors, R B Shah Group, design experience center" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="hero about-hero" style={{ 
                height: '70vh',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative',
                overflow: 'hidden',
                paddingTop: '120px'
            }}>
                <div className="hero-content" style={{ position: 'relative', zIndex: 2, textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)', padding: '20px', textAlign: 'center' }}>
                    <h1 className="about-hero-title" style={{ fontSize: 'clamp(38px, 8vw, 72px)', fontWeight: '300', color: '#F5F1ED', lineHeight: '1.2', marginBottom: '20px' }}>
                        About Hav'n Interior
                    </h1>
                    <p className="about-hero-subtitle hero-subtitle" style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#F5F1ED', fontWeight: '400', marginBottom: '35px', lineHeight: '1.6' }}>
                        Crafting Experiences Since 1980
                    </p>
                    <div className="responsive-flex" style={{ 
                        marginTop: '40px', 
                        display: 'flex', 
                        gap: '30px', 
                        justifyContent: 'center',
						alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <div className="hero-stat-item stat-card" style={{ textAlign: 'center', minWidth: '120px' }}>
                            <div className="counter-years" style={{ fontSize: 'clamp(30px, 6vw, 42px)', fontWeight: '300', color: 'var(--accent-color)' }}>0+</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px' }}>YEARS LEGACY</div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '50px', background: 'rgba(201, 169, 110, 0.5)' }} />
                        <div className="hero-stat-item stat-card" style={{ textAlign: 'center', minWidth: '120px' }}>
                            <div className="counter-studios" style={{ fontSize: 'clamp(30px, 6vw, 42px)', fontWeight: '300', color: 'var(--accent-color)' }}>0+</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px' }}>STUDIOS</div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '50px', background: 'rgba(201, 169, 110, 0.5)' }} />
                        <div className="hero-stat-item stat-card" style={{ textAlign: 'center', minWidth: '120px' }}>
                            <div className="counter-projects" style={{ fontSize: 'clamp(30px, 6vw, 42px)', fontWeight: '300', color: 'var(--accent-color)' }}>0+</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.9)', letterSpacing: '1px' }}>SEGMENTS</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="fade-in-section" style={{ background: '#0F0F0F', padding: '50px 5%' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '30px', color: 'var(--accent-color)' }}>
                        Who We Are
                    </h2>
                    <p style={{ fontSize: '22px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.9)', marginBottom: '40px', fontWeight: '300' }}>
                        Welcome to <strong style={{ color: 'var(--accent-color)', fontWeight: '500' }}>HAV'N Interiors & Global Sourcing</strong>, Chennai's premier design destination. 
                        Backed by the <strong style={{ fontWeight: '500' }}>R B Shah Group's 45 years of expertise</strong>, we offer a truly unique Design Experience Centre 
                        where creativity, collaboration, and commerce converge.
                    </p>
                    <div style={{ 
                        maxWidth: '900px', 
                        margin: '0 auto', 
                        padding: '40px', 
                        background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.1) 0%, rgba(201, 169, 110, 0.05) 100%)',
                        borderRadius: '20px',
                        border: '1px solid rgba(201, 169, 110, 0.2)'
                    }}>
                        <p style={{ fontSize: '18px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.85)', margin: 0 }}>
                            We are not just interior designers – we are storytellers, innovators, and craftsmen dedicated to transforming 
                            spaces into living experiences. Our approach combines global sourcing capabilities with local craftsmanship, 
                            bringing you the best of both worlds.
                        </p>
                    </div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="stagger-item" style={{ padding: '50px 5%' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 className="section-title">Our Vision & Mission</h2>
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', marginTop: '60px' }}>
                        {/* Vision */}
                        <div className="stagger-child" style={{ 
                            background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(0, 0, 0, 0.3) 100%)', 
                            padding: '50px', 
                            borderRadius: '20px', 
                            border: '2px solid rgba(201, 169, 110, 0.3)',
                            transition: 'all 0.4s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(201, 169, 110, 0.3)';
                            e.currentTarget.style.borderColor = 'var(--accent-color)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                        }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" style={{ marginBottom: '20px' }}>
                                <circle cx="12" cy="12" r="10"/>
                                <circle cx="12" cy="12" r="6"/>
                                <circle cx="12" cy="12" r="2"/>
                            </svg>
                            <h3 style={{ fontSize: '32px', color: 'var(--accent-color)', marginBottom: '20px', fontWeight: '300' }}>Our Vision</h3>
                            <p style={{ fontSize: '17px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.9)', marginBottom: '25px' }}>
                                To pioneer India's first free co-working café for interior designers and architects, a space where 
                                <strong style={{ color: 'var(--accent-color)', fontWeight: '500' }}> creativity, collaboration, and commerce converge</strong>. 
                                With access to an extensive live material library and our innovative Quick Projection Planning Tool, 
                                we enable professionals to design, estimate, and deliver with clarity and confidence.
                            </p>
                            
                            <h4 style={{ fontSize: '22px', color: 'var(--accent-color)', marginBottom: '15px', marginTop: '30px', fontWeight: '400' }}>Vision to Integrate</h4>
                            <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.85)', marginBottom: '25px' }}>
                                Our vision is to elevate professionalism in the interior design industry by fostering a seamlessly connected global and local ecosystem of designers, dealers, distributors, manufacturers, and clients.
                            </p>

                            <h4 style={{ fontSize: '22px', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: '400' }}>Unifying the Ecosystem</h4>
                            <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.85)', marginBottom: '15px' }}>
                                We aim to bridge the leaks in the industry by building strong associations across the value chain — addressing challenges such as:
                            </p>
                            <ul style={{ fontSize: '15px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', marginLeft: '20px', marginBottom: '15px' }}>
                                <li style={{ marginBottom: '8px' }}>Credit risks that weaken businesses</li>
                                <li style={{ marginBottom: '8px' }}>Misaligned incentives to contractors</li>
                                <li style={{ marginBottom: '8px' }}>Unhealthy competition between distributors and dealers</li>
                            </ul>
                            <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.85)' }}>
                                By uniting every stakeholder under one trusted platform, we envision an industry that is transparent, efficient, and future-ready.
                            </p>
                        </div>

                        {/* Mission */}
                        <div className="stagger-child" style={{ 
                            background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(0, 0, 0, 0.3) 100%)', 
                            padding: '50px', 
                            borderRadius: '20px', 
                            border: '2px solid rgba(201, 169, 110, 0.3)',
                            transition: 'all 0.4s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(201, 169, 110, 0.3)';
                            e.currentTarget.style.borderColor = 'var(--accent-color)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                        }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2" style={{ marginBottom: '20px' }}>
                                <path d="M12 2v20"/>
                                <path d="m15 5-3-3-3 3"/>
                                <path d="m2 12 3 3 3-3"/>
                                <path d="m22 12-3 3-3-3"/>
                            </svg>
                            <h3 style={{ fontSize: '32px', color: 'var(--accent-color)', marginBottom: '20px', fontWeight: '300' }}>Our Mission</h3>
                            <p style={{ fontSize: '17px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.9)', marginBottom: '30px' }}>
                                Our mission is to <strong style={{ color: 'var(--accent-color)', fontWeight: '500' }}>empower India's interior design and architecture community</strong> by 
                                providing a trusted platform that fosters creativity, collaboration, and commerce. We are dedicated to unifying the ecosystem of designers, dealers, manufacturers, and clients, 
                                ensuring a transparent, efficient, and future-ready industry.
                            </p>

                            <h4 style={{ fontSize: '22px', color: 'var(--accent-color)', marginBottom: '15px', fontWeight: '400' }}>We Will Achieve This By:</h4>
                            <ul style={{ fontSize: '15px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.85)', marginLeft: '20px', marginTop: '15px' }}>
                                <li style={{ marginBottom: '12px' }}>Crafting inspiring spaces through a curated selection of exceptional products and services</li>
                                <li style={{ marginBottom: '12px' }}>Leveraging our global resources and strategic collaborations to bring the best materials and tools to the Indian market</li>
                                <li style={{ marginBottom: '12px' }}>Serving clients across India and building strong, lasting associations that bridge existing gaps and mitigate risks</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="fade-in-section" style={{ background: '#0F0F0F', padding: '50px 5%' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '40px', textAlign: 'center', color: 'var(--accent-color)' }}>
                        Our Story
                    </h2>
                    <div className="responsive-grid grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }}>
                        <div>
                            <div className="timeline-item" style={{ marginBottom: '30px' }}>
                                <h3 style={{ fontSize: '24px', color: 'var(--accent-color)', marginBottom: '15px' }}>1980 - The Foundation</h3>
                                <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.7)' }}>
                                    R B Shah Group established its roots in the interior design industry, bringing international 
                                    design standards to India.
                                </p>
                            </div>
                            <div className="timeline-item" style={{ marginBottom: '30px' }}>
                                <h3 style={{ fontSize: '24px', color: 'var(--accent-color)', marginBottom: '15px' }}>2010 - Expansion</h3>
                                <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.7)' }}>
                                    Expanded operations to include global sourcing, establishing partnerships with premium 
                                    manufacturers across Europe and Asia.
                                </p>
                            </div>
                            <div className="timeline-item">
                                <h3 style={{ fontSize: '24px', color: 'var(--accent-color)', marginBottom: '15px' }}>2020 - HAV'N Interiors</h3>
                                <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.7)' }}>
                                    Launched HAV'N Interiors as Chennai's premier Design Experience Centre, featuring 8 specialized 
                                    studios under one roof.
                                </p>
                            </div>
                        </div>
                        <div 
                            className="story-image-container"
                            style={{ 
                                height: '500px', 
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid rgba(201, 169, 110, 0.3)',
                                position: 'relative',
                                overflow: 'hidden',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                const img = e.currentTarget.querySelector('.story-image-bg');
                                if (img) img.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                const img = e.currentTarget.querySelector('.story-image-bg');
                                if (img) img.style.transform = 'scale(1)';
                            }}
                        >
                            <div 
                                className="story-image-bg"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.1) 0%, rgba(0, 0, 0, 0.5) 100%), url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&q=80)',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    transition: 'transform 0.6s ease',
                                    transform: 'scale(1)'
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: '30px',
                                left: '30px',
                                right: '30px',
                                padding: '25px',
                                background: 'rgba(15, 15, 15, 0.85)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '12px',
                                border: '1px solid rgba(201, 169, 110, 0.3)'
                            }}>
                                <p style={{ 
                                    color: 'var(--accent-color)', 
                                    fontSize: '18px',
                                    fontWeight: '500',
                                    marginBottom: '8px'
                                }}>
                                    45+ Years of Excellence
                                </p>
                                <p style={{ 
                                    color: 'rgba(245, 241, 237, 0.9)', 
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    margin: 0
                                }}>
                                    From 1980 to Today - Transforming spaces, building legacies
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our 8 Studios */}
            <section className="stagger-item" style={{ 
                position: 'relative',
                padding: '50px 5%',
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.design})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <h2 className="section-title" style={{ marginTop: '0', marginBottom: '20px' }}>Our 8 Design Studios</h2>
                    <p style={{ textAlign: 'center', fontSize: '20px', color: 'rgba(245, 241, 237, 0.8)', maxWidth: '800px', margin: '0 auto 60px' }}>
                        Each studio is a specialized domain of expertise, curated to provide comprehensive solutions for every aspect of interior design.
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px', width: '100%' }}>
                        {[
                            { name: 'Lumi Ceiling', desc: 'Ceiling Design', icon: '' },
                            { name: 'Craftcove', desc: 'Wood & Doors', icon: '' },
                            { name: 'Thread', desc: 'Textiles & Furnishing', icon: '' },
                            { name: 'Artistryroom', desc: 'Decorative Elements', icon: '' },
                            { name: 'Global Nest', desc: 'Furniture Sourcing', icon: '' },
                            { name: 'Surfacestyle', desc: 'Flooring Solutions', icon: '' },
                            { name: 'Locknkey', desc: 'Hardware & Fixtures', icon: '' },
                            { name: 'Shape Studio', desc: 'Stone & Marble', icon: '' }
                        ].map((studio, index) => (
                            <div key={index} className="stagger-child" style={{
                                background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.25) 0%, rgba(0, 0, 0, 0.85) 100%)',
                                backdropFilter: 'blur(10px)',
                                padding: '40px 30px',
                                borderRadius: '16px',
                                textAlign: 'center',
                                border: '2px solid rgba(201, 169, 110, 0.3)',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
								minHeight: '250px',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 20px 40px rgba(201, 169, 110, 0.4)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.35) 0%, rgba(0, 0, 0, 0.9) 100%)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.25) 0%, rgba(0, 0, 0, 0.85) 100%)';
                            }}>
                                <div className="studio-icon" style={{ fontSize: '56px', marginBottom: '20px', filter: 'drop-shadow(0 4px 8px rgba(201, 169, 110, 0.3))' }}>{studio.icon}</div>
                                <h3 style={{ fontSize: '24px', color: 'var(--accent-color)', marginBottom: '12px', fontWeight: '400' }}>
                                    {studio.name}
                                </h3>
                                <p style={{ fontSize: '15px', color: 'rgba(245, 241, 237, 0.8)', fontWeight: '300', lineHeight: '1.6' }}>
                                    {studio.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        marginTop: '60px' 
                    }}>
                        <Link to="/studios" style={{
                            display: 'inline-block',
                            padding: '18px 50px',
                            background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
                            color: '#000',
                            fontSize: '16px',
                            fontWeight: '600',
                            borderRadius: '50px',
                            textDecoration: 'none',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 15px rgba(201, 169, 110, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-3px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(201, 169, 110, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 15px rgba(201, 169, 110, 0.3)';
                        }}>
                            Explore All Studios →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="stagger-item" style={{ background: '#0F0F0F', padding: '50px 5%' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <h2 className="section-title">Our Core Values</h2>
                    <div className="responsive-grid grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '40px', marginTop: '60px' }}>
                        {[
                            {
                                icon: 'star',
                                title: 'Excellence',
                                desc: 'We pursue perfection in every project, ensuring the highest standards of quality and craftsmanship.',
                                image: PLACEHOLDER_IMAGES.design
                            },
                            {
                                icon: 'handshake',
                                title: 'Collaboration',
                                desc: 'We believe in the power of teamwork, bringing together diverse talents to create extraordinary results.',
                                image: PLACEHOLDER_IMAGES.team
                            },
                            {
                                icon: 'lightbulb',
                                title: 'Innovation',
                                desc: 'We constantly explore new materials, techniques, and technologies to push the boundaries of design.',
                                image: PLACEHOLDER_IMAGES.lighting
                            },
                            {
                                icon: 'globe',
                                title: 'Sustainability',
                                desc: 'We are committed to eco-friendly practices and sourcing sustainable materials for a better tomorrow.',
                                image: PLACEHOLDER_IMAGES.materials
                            },
                            {
                                icon: 'shield',
                                title: 'Integrity',
                                desc: 'We conduct business with honesty, transparency, and respect for all our clients and partners.',
                                image: PLACEHOLDER_IMAGES.consultation
                            },
                            {
                                icon: 'target',
                                title: 'Customer Focus',
                                desc: 'Your vision is our priority. We listen, understand, and deliver solutions that exceed expectations.',
                                image: PLACEHOLDER_IMAGES.office
                            }
                        ].map((value, index) => (
                            <div key={index} className="stagger-child core-value-card" style={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '16px',
                                border: '2px solid rgba(201, 169, 110, 0.3)',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(0, 0, 0, 0.8) 100%)',
                                backdropFilter: 'blur(10px)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-10px)';
                                e.currentTarget.style.boxShadow = '0 20px 40px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.25) 0%, rgba(0, 0, 0, 0.9) 100%)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(0, 0, 0, 0.8) 100%)';
                            }}>
                                {/* Front Image */}
                                <div style={{
                                    height: '200px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <img
                                        src={value.image}
                                        alt={value.title}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.3s ease'
                                        }}
                                        className="core-value-image"
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '15px',
                                        right: '15px',
                                        background: 'rgba(0, 0, 0, 0.6)',
                                        borderRadius: '50%',
                                        width: '50px',
                                        height: '50px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                        {value.icon === 'star' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                            </svg>
                                        )}
                                        {value.icon === 'handshake' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"/>
                                                <path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/>
                                                <path d="m2 13 6 6"/>
                                            </svg>
                                        )}
                                        {value.icon === 'lightbulb' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <line x1="9" y1="18" x2="15" y2="18"/>
                                                <line x1="10" y1="22" x2="14" y2="22"/>
                                                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
                                            </svg>
                                        )}
                                        {value.icon === 'globe' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="2" y1="12" x2="22" y2="12"/>
                                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                            </svg>
                                        )}
                                        {value.icon === 'shield' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                            </svg>
                                        )}
                                        {value.icon === 'target' && (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"/>
                                                <circle cx="12" cy="12" r="6"/>
                                                <circle cx="12" cy="12" r="2"/>
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div style={{
                                    padding: '30px',
                                    textAlign: 'center',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                    <h3 style={{ fontSize: '24px', color: 'var(--accent-color)', marginBottom: '15px' }}>
                                        {value.title}
                                    </h3>
                                    <p style={{ fontSize: '15px', lineHeight: '1.7', color: 'rgba(245, 241, 237, 0.9)' }}>
                                        {value.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* 73% Solution Section */}
            <section className="solution-73" style={{ background: '#0F0F0F', padding: '120px 60px', position: 'relative' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>
                        {/* Left Side - Table */}
                        <div>
                            <table className="solution-table">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>% Allocation</th>
                                        <th>Cost (₹ in Lakhs)</th>
                                        <th>Our Coverage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>1. Civil Modifications (Partition Walls, etc.)</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>2. False Ceiling & POP Works</td>
                                        <td>8%</td>
                                        <td>₹8.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>3. Painting & Wall Finishes (Texture, Wallpapers)</td>
                                        <td>7%</td>
                                        <td>₹7.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>4. Flooring (Tiles, Wooden, Stone Inlays)</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>5. Electrical Work & Lighting</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>6. Plumbing Alteration & Sanitary Fixtures</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr>
                                        <td>7. Modular Kitchen</td>
                                        <td>8%</td>
                                        <td>₹8.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>8. Wardrobes & Storage Units</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>9. Loose Furniture (Beds, Sofas, Dining, etc.)</td>
                                        <td>10%</td>
                                        <td>₹10.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>10. Soft Furnishing (Curtains, Blinds, Rugs)</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>11. Appliances (AC, Chimney, Hob, etc.)</td>
                                        <td>5%</td>
                                        <td>₹5.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>12. Décor, Art, Mirrors, Accessories</td>
                                        <td>4%</td>
                                        <td>₹4.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>13. Project Management & Design Fees</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="we-do">■ We Do</span></td>
                                    </tr>
                                    <tr>
                                        <td>14. Contingency / Buffer</td>
                                        <td>6%</td>
                                        <td>₹6.00</td>
                                        <td><span className="not-covered">✕ Not Covered</span></td>
                                    </tr>
                                    <tr className="total-row">
                                        <td><strong>Total</strong></td>
                                        <td><strong>100%</strong></td>
                                        <td><strong>₹100.00 Lakh</strong></td>
                                        <td><strong>73% Covered by Us</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Right Side - Content */}
                        <div style={{ paddingTop: '20px' }}>
                            <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '20px', color: 'var(--accent-color)' }}>
                                The 73% Solution
                            </h2>
                            <h3 style={{ ...TYPOGRAPHY.h3, marginBottom: '30px' }}>
                                A New Standard for Project Success
                            </h3>
                            <p style={{ ...TYPOGRAPHY.body, marginBottom: '30px', lineHeight: '1.8' }}>
                                We empower with a wide coverage of your interior requirements
                            </p>

                            <div style={{ background: 'rgba(201, 169, 110, 0.1)', padding: '30px', borderRadius: '12px', border: '1px solid rgba(201, 169, 110, 0.3)', marginBottom: '30px' }}>
                                <h4 style={{ fontSize: '18px', color: 'var(--accent-color)', marginBottom: '15px' }}>
                                    For a villa project of 4,500 sq.ft.<br/>
                                    (₹100 Lakh Budget),
                                </h4>
                                <p style={{ ...TYPOGRAPHY.body, lineHeight: '1.7' }}>
                                    here's how the cost allocation works. As your distributor partner, we cover <strong style={{ color: 'var(--accent-color)' }}>73% of the project requirements</strong> – giving you unmatched advantage in cross-selling and project execution.
                                </p>
                            </div>

							{/* <div style={{ background: 'rgba(255, 77, 77, 0.1)', padding: '25px', borderRadius: '12px', border: '1px solid rgba(255, 77, 77, 0.3)' }}>
                                <p style={{ fontSize: '15px', color: 'rgba(245, 241, 237, 0.9)', lineHeight: '1.6', marginBottom: '10px' }}>
                                    <strong style={{ color: '#ff6b6b' }}>Note:</strong> Need the half house with all the percentage marking
                                </p>
                                <p style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.6' }}>
                                    We provide comprehensive support across 73% of your project needs, ensuring seamless execution and maximum value.
                                </p>
                            </div> */}
                        </div>
                    </div>
                </div>
            </section> 
			

            {/* Why Choose Us */}
            <section className="stagger-item" style={{ padding: '50px 5%' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <h2 className="section-title" style={{ marginTop: '0', marginBottom: '60px' }}>Why Choose HAV'N Interiors</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                        {[
                            {
                                icon: 'check',
                                title: '45+ Years of Expertise',
                                desc: 'Backed by R B Shah Group\'s rich legacy in the interior design industry.',
                                image: PLACEHOLDER_IMAGES.consultation
                            },
                            {
                                icon: 'check',
                                title: 'Global Sourcing Network',
                                desc: 'Access to premium materials and products from leading manufacturers worldwide.',
                                image: PLACEHOLDER_IMAGES.materials
                            },
                            {
                                icon: 'check',
                                title: '8 Specialized Studios',
                                desc: 'Comprehensive solutions for every aspect of interior design under one roof.',
                                image: PLACEHOLDER_IMAGES.design
                            },
                            {
                                icon: 'check',
                                title: 'Turnkey Solutions',
                                desc: 'End-to-end project management from concept to completion.',
                                image: PLACEHOLDER_IMAGES.planning
                            },
                            {
                                icon: 'check',
                                title: 'Dedicated Project Teams',
                                desc: 'Personalized attention with a dedicated team for each project.',
                                image: PLACEHOLDER_IMAGES.team
                            },
                            {
                                icon: 'check',
                                title: 'Transparent Pricing',
                                desc: 'Clear, upfront pricing with no hidden costs or surprises.',
                                image: PLACEHOLDER_IMAGES.office
                            }
                        ].map((item, index) => (
                            <div key={index} className="stagger-child why-choose-card" style={{ 
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '16px',
                                border: '2px solid rgba(201, 169, 110, 0.3)',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(0, 0, 0, 0.8) 100%)',
                                backdropFilter: 'blur(10px)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-5px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.25) 0%, rgba(0, 0, 0, 0.9) 100%)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(201, 169, 110, 0.15) 0%, rgba(0, 0, 0, 0.8) 100%)';
                            }}>
                                {/* Front Image */}
                                <div style={{
                                    height: '180px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.3s ease'
                                        }}
                                        className="why-choose-image"
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '15px',
                                        right: '15px',
                                        fontSize: '24px',
                                        background: 'rgba(0, 0, 0, 0.6)',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backdropFilter: 'blur(10px)',
                                        color: 'var(--accent-color)'
                                    }}>
                                        {item.icon}
                                    </div>
                                </div>

                                {/* Content */}
                                <div style={{ 
                                    padding: '25px',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                    <h3 style={{ fontSize: '22px', marginBottom: '10px', color: 'var(--accent-color)' }}>
                                        {item.title}
                                    </h3>
                                    <p style={{ fontSize: '16px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.9)' }}>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="fade-in-section" style={{ 
                textAlign: 'center', 
                background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
                padding: '50px 5%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '48px', fontWeight: '300', marginBottom: '25px', color: '#000', marginTop: '0' }}>
                        Let's Create Something Extraordinary
                    </h2>
                    <p style={{ fontSize: '18px', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '35px', maxWidth: '700px', margin: '0 auto 35px' }}>
                        Join us in transforming spaces into living experiences. Schedule your consultation today.
                    </p>
                <Link to="/contact" style={{
                    display: 'inline-block',
                    padding: '18px 50px',
                    background: '#000',
                    color: 'var(--accent-color)',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    border: '2px solid #000',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                }}
                onMouseEnter={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#000';
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = '#000';
                    e.target.style.color = 'var(--accent-color)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                }}>
                    Get in Touch
                </Link>
                </div>
            </section>

            {/* Team & Directors Section */}
            <section className="stagger-item" style={{ 
                background: '#0F0F0F', 
                padding: '80px 5%',
                position: 'relative',
                display:'none',
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 className="section-title" style={{ marginBottom: '20px' }}>Our Leadership Team</h2>
                    <p style={{ 
                        textAlign: 'center', 
                        fontSize: '18px', 
                        color: 'rgba(245, 241, 237, 0.8)', 
                        maxWidth: '800px', 
                        margin: '0 auto 60px',
                        lineHeight: '1.6'
                    }}>
                        Meet the visionary leaders who drive innovation and excellence at HAV'N Interiors
                    </p>

                    {/* Board of Directors Section */}
                    <div style={{ marginBottom: '100px' }}>
                        <h3 style={{ 
                            fontSize: '32px', 
                            color: 'var(--accent-color)', 
                            textAlign: 'center', 
                            marginBottom: '60px',
                            fontWeight: '300'
                        }}>
                            Board of Directors
                        </h3>
                        
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '50px'
                        }}>
                            {directorsData.map((director) => (
                                <div 
                                    key={director.id}
                                    className="stagger-child director-card" 
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(0, 0, 0, 0.5) 100%)',
                                        borderRadius: '20px',
                                        padding: '50px',
                                        border: '2px solid rgba(201, 169, 110, 0.3)',
                                        transition: 'all 0.4s ease'
                                    }}
                                >
                                    {/* Header Section */}
                                    <div style={{ 
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '40px',
                                        marginBottom: '40px',
                                        paddingBottom: '30px',
                                        borderBottom: '2px solid rgba(201, 169, 110, 0.2)'
                                    }}>
                                        <div style={{
                                            width: '120px',
                                            height: '120px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '48px',
                                            color: '#000',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}>
                                            {director.initials}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ 
                                                fontSize: '32px', 
                                                color: 'var(--accent-color)', 
                                                marginBottom: '10px',
                                                fontWeight: '300'
                                            }}>
                                                {director.name}
                                            </h4>
                                            <p style={{ 
                                                fontSize: '18px', 
                                                color: 'rgba(245, 241, 237, 0.9)', 
                                                marginBottom: '8px',
                                                fontWeight: '500'
                                            }}>
                                                {director.title}
                                            </p>
                                            <p style={{ 
                                                fontSize: '16px', 
                                                color: 'rgba(201, 169, 110, 0.9)', 
                                                fontStyle: 'italic'
                                            }}>
                                                {director.role}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Full Bio */}
                                    <div style={{ marginBottom: '30px' }}>
                                        {director.fullBio.split('\n\n').map((paragraph, index) => (
                                            <p key={index} style={{
                                                fontSize: '16px',
                                                color: 'rgba(245, 241, 237, 0.9)',
                                                lineHeight: '1.8',
                                                marginBottom: '20px',
                                                textAlign: 'justify'
                                            }}>
                                                {paragraph}
                                            </p>
                                        ))}
                                    </div>

                                    {/* Strengths/Expertise */}
                                    {director.strengths && (
                                        <div style={{ marginBottom: '30px' }}>
                                            <h5 style={{
                                                fontSize: '20px',
                                                color: 'var(--accent-color)',
                                                marginBottom: '20px',
                                                fontWeight: '400'
                                            }}>
                                                {director.id === 'saargi' ? 'Areas of Expertise:' : 'Key Strengths:'}
                                            </h5>
                                            <ul style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0
                                            }}>
                                                {director.strengths.map((strength, index) => (
                                                    <li key={index} style={{
                                                        fontSize: '16px',
                                                        color: 'rgba(245, 241, 237, 0.9)',
                                                        marginBottom: '12px',
                                                        paddingLeft: '30px',
                                                        position: 'relative',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            color: 'var(--accent-color)',
                                                            fontSize: '20px'
                                                        }}>
                                                            •
                                                        </span>
                                                        {strength}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* RBSHAH Responsibilities (Tarun) */}
                                    {director.rbshahResponsibilities && (
                                        <div style={{ marginBottom: '30px' }}>
                                            <h5 style={{
                                                fontSize: '20px',
                                                color: 'var(--accent-color)',
                                                marginBottom: '20px',
                                                fontWeight: '400'
                                            }}>
                                                As President of RBSHAH Group (est. 1981), he spearheads strategic vision, alliances, and business development across:
                                            </h5>
                                            <ul style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0
                                            }}>
                                                {director.rbshahResponsibilities.map((item, index) => (
                                                    <li key={index} style={{
                                                        fontSize: '16px',
                                                        color: 'rgba(245, 241, 237, 0.9)',
                                                        marginBottom: '12px',
                                                        paddingLeft: '30px',
                                                        position: 'relative',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            color: 'var(--accent-color)',
                                                            fontSize: '20px'
                                                        }}>
                                                            •
                                                        </span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Havn Responsibilities (Tarun) */}
                                    {director.havnResponsibilities && (
                                        <div style={{ marginBottom: '30px' }}>
                                            <h5 style={{
                                                fontSize: '20px',
                                                color: 'var(--accent-color)',
                                                marginBottom: '20px',
                                                fontWeight: '400'
                                            }}>
                                                At Havn, Tarun drives:
                                            </h5>
                                            <ul style={{
                                                listStyle: 'none',
                                                padding: 0,
                                                margin: 0
                                            }}>
                                                {director.havnResponsibilities.map((item, index) => (
                                                    <li key={index} style={{
                                                        fontSize: '16px',
                                                        color: 'rgba(245, 241, 237, 0.9)',
                                                        marginBottom: '12px',
                                                        paddingLeft: '30px',
                                                        position: 'relative',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            color: 'var(--accent-color)',
                                                            fontSize: '20px'
                                                        }}>
                                                            •
                                                        </span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Additional Info (Kunal) */}
                                    {director.additional && (
                                        <div style={{ marginBottom: '30px' }}>
                                            {director.additional.split('\n\n').map((paragraph, index) => (
                                                <div key={index}>
                                                    {paragraph.split('\n').map((line, lineIndex) => (
                                                        <p key={lineIndex} style={{
                                                            fontSize: '16px',
                                                            color: 'rgba(245, 241, 237, 0.9)',
                                                            lineHeight: '1.8',
                                                            marginBottom: '8px',
                                                            textAlign: 'justify'
                                                        }}>
                                                            {line}
                                                        </p>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Achievements */}
                                    {director.achievements && (
                                        <div style={{ 
                                            marginBottom: '30px',
                                            padding: '25px',
                                            background: 'rgba(201, 169, 110, 0.15)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(201, 169, 110, 0.3)'
                                        }}>
                                            <p style={{
                                                fontSize: '16px',
                                                color: 'rgba(245, 241, 237, 0.95)',
                                                lineHeight: '1.8',
                                                textAlign: 'justify',
                                                margin: 0
                                            }}>
                                                {director.achievements}
                                            </p>
                                        </div>
                                    )}

                                    {/* Closing Statement */}
                                    {director.closing && (
                                        <div style={{
                                            paddingTop: '25px',
                                            borderTop: '1px solid rgba(201, 169, 110, 0.2)'
                                        }}>
                                            <p style={{
                                                fontSize: '16px',
                                                color: 'rgba(245, 241, 237, 0.95)',
                                                lineHeight: '1.8',
                                                textAlign: 'justify',
                                                fontStyle: 'italic'
                                            }}>
                                                {director.closing}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Core Team Section - Hidden */}
                    <div style={{ display: 'none' }}>
                        <h3 style={{ 
                            fontSize: '28px', 
                            color: 'var(--accent-color)', 
                            textAlign: 'center', 
                            marginBottom: '50px',
                            fontWeight: '300'
                        }}>
                            Core Team
                        </h3>
                        
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                            gap: '30px'
                        }}>
                            {/* Team Member 1 */}
                            <div className="stagger-child core-team-card" style={{
                                position: 'relative',
                                borderRadius: '16px',
                                textAlign: 'center',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                height: '450px',
                                border: '2px solid rgba(201, 169, 110, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                            }}>
                                {/* Full Background Image */}
                                <img
                                    src={PLACEHOLDER_IMAGES.teamMember1}
                                    alt="Arjun Sharma"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        transition: 'transform 0.3s ease'
                                    }}
                                    className="core-team-image"
                                />
                                
                                {/* Gradient Overlay */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    padding: '30px 20px',
                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%)',
                                    zIndex: 1
                                }}>
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        margin: '0 auto 15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        color: '#000',
                                        fontWeight: 'bold'
                                    }}>
                                        AS
                                    </div>
                                    <h4 style={{ 
                                        fontSize: '20px', 
                                        color: 'var(--accent-color)', 
                                        marginBottom: '8px',
                                        fontWeight: '300'
                                    }}>
                                        Arjun Sharma
                                    </h4>
                                    <p style={{ 
                                        fontSize: '14px', 
                                        color: 'rgba(245, 241, 237, 0.9)', 
                                        marginBottom: '10px',
                                        fontWeight: '500'
                                    }}>
                                        Head of Design
                                    </p>
                                    <p style={{ 
                                        fontSize: '12px', 
                                        color: 'rgba(245, 241, 237, 0.7)', 
                                        lineHeight: '1.5'
                                    }}>
                                        Leading our creative vision with 15+ years of design expertise
                                    </p>
                                </div>
                            </div>

                            {/* Team Member 2 */}
                            <div className="stagger-child core-team-card" style={{
                                position: 'relative',
                                borderRadius: '16px',
                                textAlign: 'center',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                height: '450px',
                                border: '2px solid rgba(201, 169, 110, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                            }}>
                                {/* Full Background Image */}
                                <img
                                    src={PLACEHOLDER_IMAGES.teamMember2}
                                    alt="Meera Kumar"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        transition: 'transform 0.3s ease'
                                    }}
                                    className="core-team-image"
                                />
                                
                                {/* Gradient Overlay */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    padding: '30px 20px',
                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%)',
                                    zIndex: 1
                                }}>
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        margin: '0 auto 15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        color: '#000',
                                        fontWeight: 'bold'
                                    }}>
                                        MK
                                    </div>
                                    <h4 style={{ 
                                        fontSize: '20px', 
                                        color: 'var(--accent-color)', 
                                        marginBottom: '8px',
                                        fontWeight: '300'
                                    }}>
                                        Meera Kumar
                                    </h4>
                                    <p style={{ 
                                        fontSize: '14px', 
                                        color: 'rgba(245, 241, 237, 0.9)', 
                                        marginBottom: '10px',
                                        fontWeight: '500'
                                    }}>
                                        Project Manager
                                    </p>
                                    <p style={{ 
                                        fontSize: '12px', 
                                        color: 'rgba(245, 241, 237, 0.7)', 
                                        lineHeight: '1.5'
                                    }}>
                                        Ensuring flawless execution of every design project
                                    </p>
                                </div>
                            </div>

                            {/* Team Member 3 */}
                            <div className="stagger-child core-team-card" style={{
                                position: 'relative',
                                borderRadius: '16px',
                                textAlign: 'center',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                height: '450px',
                                border: '2px solid rgba(201, 169, 110, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                            }}>
                                {/* Full Background Image */}
                                <img
                                    src={PLACEHOLDER_IMAGES.teamMember3}
                                    alt="Vikram Patel"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        transition: 'transform 0.3s ease'
                                    }}
                                    className="core-team-image"
                                />
                                
                                {/* Gradient Overlay */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    padding: '30px 20px',
                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%)',
                                    zIndex: 1
                                }}>
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        margin: '0 auto 15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        color: '#000',
                                        fontWeight: 'bold'
                                    }}>
                                        VP
                                    </div>
                                    <h4 style={{ 
                                        fontSize: '20px', 
                                        color: 'var(--accent-color)', 
                                        marginBottom: '8px',
                                        fontWeight: '300'
                                    }}>
                                        Vikram Patel
                                    </h4>
                                    <p style={{ 
                                        fontSize: '14px', 
                                        color: 'rgba(245, 241, 237, 0.9)', 
                                        marginBottom: '10px',
                                        fontWeight: '500'
                                    }}>
                                        Client Relations Manager
                                    </p>
                                    <p style={{ 
                                        fontSize: '12px', 
                                        color: 'rgba(245, 241, 237, 0.7)', 
                                        lineHeight: '1.5'
                                    }}>
                                        Building lasting relationships with our valued clients
                                    </p>
                                </div>
                            </div>

                            {/* Team Member 4 */}
                            <div className="stagger-child core-team-card" style={{
                                position: 'relative',
                                borderRadius: '16px',
                                textAlign: 'center',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                height: '450px',
                                border: '2px solid rgba(201, 169, 110, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(201, 169, 110, 0.3)';
                                e.currentTarget.style.borderColor = 'var(--accent-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.3)';
                            }}>
                                {/* Full Background Image */}
                                <img
                                    src={PLACEHOLDER_IMAGES.teamMember4}
                                    alt="Suresh Reddy"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        transition: 'transform 0.3s ease'
                                    }}
                                    className="core-team-image"
                                />
                                
                                {/* Gradient Overlay */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    padding: '30px 20px',
                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%)',
                                    zIndex: 1
                                }}>
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        margin: '0 auto 15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        color: '#000',
                                        fontWeight: 'bold'
                                    }}>
                                        SR
                                    </div>
                                    <h4 style={{ 
                                        fontSize: '20px', 
                                        color: 'var(--accent-color)', 
                                        marginBottom: '8px',
                                        fontWeight: '300'
                                    }}>
                                        Suresh Reddy
                                    </h4>
                                    <p style={{ 
                                        fontSize: '14px', 
                                        color: 'rgba(245, 241, 237, 0.9)', 
                                        marginBottom: '10px',
                                        fontWeight: '500'
                                    }}>
                                        Technical Director
                                    </p>
                                    <p style={{ 
                                        fontSize: '12px', 
                                        color: 'rgba(245, 241, 237, 0.7)', 
                                        lineHeight: '1.5'
                                    }}>
                                        Overseeing technical excellence and innovation
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{ width: '180px', marginBottom: '20px', opacity: 0.8 }} />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '12px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>

            {/* CSS for Hover Effects */}
            <style jsx>{`
                .core-value-card:hover .core-value-image {
                    transform: scale(1.1);
                }
                
                .why-choose-card:hover .why-choose-image {
                    transform: scale(1.1);
                }
                
                .leadership-card:hover .leadership-image {
                    transform: scale(1.1);
                }
                
                .core-team-card:hover .core-team-image {
                    transform: scale(1.1);
                }
            `}</style>
        </div>
    );
};


export default AboutPage;

